import { AxiosResponse } from "axios";
import { all, call, fork, put, takeLatest } from "redux-saga/effects";

import { ImageFile } from "components/ImageUpload";
import { ImageType } from "entities/common";
import { PortfolioItem } from "entities/portfolios";
import { PortfolioImagesApi, PortfoliosApi, StatisticsApi } from "services/http";
import { getFetchSaga, removeFetchSaga } from "store/commonSagas";
import {
  CreatePortfolioRequestPayload,
  UpdatePortfolioRequestPayload,
  createPortfolioFailure,
  createPortfolioRequest,
  createPortfolioSuccess,
  getComplianceScoreFailure,
  getComplianceScoreRequest,
  getComplianceScoreSuccess,
  getIAQDashboardFailure,
  getIAQDashboardRequest,
  getIAQDashboardSuccess,
  getPortfolioDeviceStatisticsFailure,
  getPortfolioDeviceStatisticsRequest,
  getPortfolioDeviceStatisticsSuccess,
  getPortfolioFailure,
  getPortfolioListFailure,
  getPortfolioListRequest,
  getPortfolioListSuccess,
  getPortfolioRequest,
  getPortfolioStatisticsFailure,
  getPortfolioStatisticsRequest,
  getPortfolioStatisticsSuccess,
  getPortfolioSuccess,
  removePortfolioFailure,
  removePortfolioRequest,
  removePortfolioSuccess,
  updatePortfolioFailure,
  updatePortfolioRequest,
  updatePortfolioSuccess,
} from "store/slices/portfolioSlice";
import { showToast } from "store/slices/toastSlice";

const watchGetList = getFetchSaga(
  getPortfolioListRequest.type,
  PortfoliosApi.getList,
  getPortfolioListSuccess,
  getPortfolioListFailure,
);

const watchGetCurrent = getFetchSaga(
  getPortfolioRequest.type,
  PortfoliosApi.getCurrent,
  getPortfolioSuccess,
  getPortfolioFailure,
);

function* createPortfolioFlow(action: { type: string; payload: CreatePortfolioRequestPayload }) {
  const { body, images } = action.payload;

  try {
    const response: AxiosResponse<PortfolioItem> = yield call(PortfoliosApi.create, body);
    const portfolio = response.data;

    const filesToUpload = (images ?? [])
      .map((img: ImageFile) => img.file)
      .filter((file): file is File => Boolean(file));

    if (filesToUpload.length === 0) {
      yield put(createPortfolioSuccess(portfolio));
      return;
    }

    try {
      const uploadResponse: AxiosResponse<ImageType[]> = yield call(
        PortfolioImagesApi.upload,
        portfolio.id,
        filesToUpload,
      );
      yield put(createPortfolioSuccess({ ...portfolio, images: uploadResponse.data }));
    } catch {
      yield put(createPortfolioSuccess(portfolio));
      yield put(
        showToast({
          id: `portfolio-image-upload-failed-${portfolio.id}`,
          message: "Portfolio created, but image upload failed.",
          type: "error",
        }),
      );
    }
  } catch (error) {
    yield put(createPortfolioFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: "portfolio-create-failed",
        message: "Failed to create portfolio. Please try again later.",
        type: "error",
        duration: 3000,
      }),
    );
  }
}

function* watchCreate() {
  yield takeLatest(createPortfolioRequest.type, createPortfolioFlow);
}

function* updatePortfolioFlow(action: { type: string; payload: UpdatePortfolioRequestPayload }) {
  const { id, body, newImages, removedImageIds } = action.payload;

  try {
    const response: AxiosResponse<PortfolioItem> = yield call(PortfoliosApi.update, id, body);
    let portfolio = response.data;

    const filesToUpload = (newImages ?? [])
      .map((img: ImageFile) => img.file)
      .filter((file): file is File => Boolean(file));

    const idsToRemove = removedImageIds ?? [];

    if (filesToUpload.length === 0 && idsToRemove.length === 0) {
      yield put(updatePortfolioSuccess(portfolio));
      return;
    }

    const removeEffects = idsToRemove.map((imageId) =>
      call(function* safeRemove() {
        try {
          yield call(PortfolioImagesApi.remove, id, imageId);
          return { ok: true as const, removedId: imageId };
        } catch {
          return { ok: false as const, removedId: imageId };
        }
      }),
    );

    const hasUpload = filesToUpload.length > 0;
    const uploadEffect = hasUpload
      ? call(function* safeUpload() {
          try {
            const uploadResponse: AxiosResponse<ImageType[]> = yield call(PortfolioImagesApi.upload, id, filesToUpload);
            return { ok: true as const, images: uploadResponse.data };
          } catch {
            return { ok: false as const, images: [] as ImageType[] };
          }
        })
      : null;

    type RemoveResult = { ok: boolean; removedId: number };
    type UploadResult = { ok: boolean; images: ImageType[] };

    const parallelEffects = uploadEffect ? [all(removeEffects), uploadEffect] : [all(removeEffects)];
    const results: unknown[] = yield all(parallelEffects);

    const removeResults = results[0] as RemoveResult[];
    const uploadResult = hasUpload ? (results[1] as UploadResult) : null;

    const successfullyRemovedIds = removeResults.filter((r) => r.ok).map((r) => r.removedId);
    const uploadedImages = uploadResult?.ok ? uploadResult.images : [];
    const imageOpsFailed = removeResults.some((r) => !r.ok) || (hasUpload && !uploadResult?.ok);

    const remainingExistingImages = (portfolio.images ?? []).filter((img) => !successfullyRemovedIds.includes(img.id));

    portfolio = {
      ...portfolio,
      images: [...remainingExistingImages, ...uploadedImages],
    };

    yield put(updatePortfolioSuccess(portfolio));

    if (imageOpsFailed) {
      yield put(
        showToast({
          id: `portfolio-image-update-failed-${id}`,
          message: "Portfolio updated, but some image changes failed.",
          type: "error",
        }),
      );
    }
  } catch (error) {
    yield put(updatePortfolioFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: "portfolio-update-failed",
        message: "Failed to update portfolio. Please try again later.",
        type: "error",
        duration: 3000,
      }),
    );
  }
}

function* watchUpdate() {
  yield takeLatest(updatePortfolioRequest.type, updatePortfolioFlow);
}

const watchRemove = removeFetchSaga(
  removePortfolioRequest.type,
  PortfoliosApi.remove,
  removePortfolioSuccess,
  removePortfolioFailure,
);

const watchGetStatistics = getFetchSaga(
  getPortfolioStatisticsRequest.type,
  StatisticsApi.getPortfolioStatistics,
  getPortfolioStatisticsSuccess,
  getPortfolioStatisticsFailure,
);

const watchGetComplianceScore = getFetchSaga(
  getComplianceScoreRequest.type,
  PortfoliosApi.getComplianceScore,
  getComplianceScoreSuccess,
  getComplianceScoreFailure,
);

const watchGetIAQDashboard = getFetchSaga(
  getIAQDashboardRequest.type,
  PortfoliosApi.getIAQDashboard,
  getIAQDashboardSuccess,
  getIAQDashboardFailure,
);

const watchGetPortfolioDeviceStatistics = getFetchSaga(
  getPortfolioDeviceStatisticsRequest.type,
  StatisticsApi.getDeviceStatistics,
  getPortfolioDeviceStatisticsSuccess,
  getPortfolioDeviceStatisticsFailure,
);

export const portfolioWatchers = [
  fork(watchGetList),
  fork(watchGetCurrent),
  fork(watchCreate),
  fork(watchUpdate),
  fork(watchRemove),
  fork(watchGetStatistics),
  fork(watchGetComplianceScore),
  fork(watchGetIAQDashboard),
  fork(watchGetPortfolioDeviceStatistics),
];
